import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import Configuration from '@/agilis/util/Configuration'
import DataTableWithGroups from '@/agilis/components/DataTableWithGroups/DataTableWithGroups.vue'
import deleteMixin from '@/agilis/components/DataTableV2/common/deleteMixin'
import FormSelectMultiple from '@/agilis/components/Form/FormSelectMultiple/FormSelectMultiple.vue'
import Vue from 'vue'
import L from 'lodash'
import jsonLogic from '@/plugins/jsonLogic'

export default {
  name: 'DeliveryPayment',
  mixins: [
    deleteMixin
  ],

  props: {
    trackerDTO: Object,
    wpGroupItems: Array,
    wpGroupItemsSpendingCap: Array,
    wpGroupData: Object,
    wpGroupDataSpendingCap: Object,
    deliveryAssociations: {
      type: Array,
      default: () => null
    }
  },

  components: {
    DataTableWithGroups
  },

  data () {
    return {
      featureId: 'deliveryPayments',
      itemsKey: 'id',
      predefinedDialog: false,
      groupDialog: false,
      paymentDialog: false,
      groupDTO: {},
      paymentDTO: {},
      predefinedPaymentsDTO: {
        type: null,
        startDate: null,
        frequency: null,
        count: null,
        order: null
      },
      paymentGroupData: {},
      formCustomComponents: {
        FormSelectMultiple
      },
      associatedOrderPayments: [],
      integratedData: {
        amountDue: 0
      },
      items: null
    }
  },

  watch: {
    orderAssociations: {
      handler (newValue, oldValue) {
        if (!L.isEqual(newValue, oldValue)) {
          this.fetchAssociatedOrderPayments()
          this.checkMissingOrderItems()
        }
      }
    },
    amountDue (newValue) {
      this.integratedData.amountDue = newValue
    },
    itemsAndAssociationsLoaded (ready) {
      if (ready) {
        this.$nextTick(() => this.checkMissingOrderItems())
      }
    }
  },

  mounted () {
    this.paymentDTO = this.emptyPaymentDTO()
    this.groupDTO = this.emptyGroupDTO()
  },

  computed: {
    ...mapGetters({
      'applicationId': 'contextStore/applicationId',
      'loading': 'loaderStore/loading',
      'selectedContext': 'contextStore/selectedContext',
      'userId': 'userDataStore/userId',
      'trackerTypeId': 'contextStore/trackerTypeId',
      'trackerId': 'contextStore/trackerId'
    }),
    title () {
      return this.wpGroupItemsSpendingCap.length > 0 ? this.$t('sales.label.deliveryPaymentsSpendingCap') : this.$t('sales.label.deliveryPayments')
    },
    predefinedFormTitle () {
      return this.$t('sales.form.predefinedPayments.title')
    },
    groupFormTitle () {
      if (this.groupDTO.id) {
        return this.$t('sales.label.deliveryPaymentsGroupEdit')
      } else {
        return this.$t('sales.label.deliveryPaymentsGroupCreate')
      }
    },
    paymentFormTitle () {
      if (this.paymentDTO[this.itemsKey]) {
        return this.$t('sales.label.deliveryPaymentEdit')
      } else {
        return this.$t('sales.label.deliveryPaymentCreate')
      }
    },
    computedWpGroupData () {
      if (this.wpGroupItemsSpendingCap?.length > 0) {
        return L.cloneDeep(this.wpGroupDataSpendingCap)
      } else {
        return L.cloneDeep(this.wpGroupData)
      }
    },
    computedWpGroupItems () {
      if (this.wpGroupItemsSpendingCap?.length > 0) {
        return L.cloneDeep(this.wpGroupItemsSpendingCap)
      } else {
        return L.cloneDeep(this.wpGroupItems)
      }
    },
    configurationTrackerType () {
      return Configuration.getTrackerType(this.applicationId, this.trackerTypeId)
    },
    configurationTrackerTypeOrder () {
      return Configuration.getTrackerType(this.applicationId, 'order')
    },
    configurationOrderResidue () {
      return this.configurationTrackerTypeOrder?.dataTable?.columns?.residue
    },
    configurationOrderResidueJsonLogic () {
      return this.configurationOrderResidue?.computed?.jsonLogic
    },
    configurationTrackerTypes () {
      return this.configurationTrackerType.trackerTypes
    },
    configuration () {
      const dataTable = this.configurationTrackerTypes.deliveryPaymentWithGrouping.dataTable
      if (this.configurationGroup && this.configurationPayment) {
        dataTable.columns = L.merge({}, this.configurationGroup.columns, this.configurationPayment.columns, dataTable.columns)
      }
      return dataTable
    },
    configurationGroup () {
      return this.configurationTrackerTypes.deliveryPaymentGroup.dataTable
    },
    configurationPayment () {
      return this.configurationTrackerTypes.deliveryPayment.dataTable
    },
    predefinedTypeFormRows () {
      return this.configuration.predefinedPayment.dataTable.formRows
    },
    groupFormRows () {
      return this.configurationGroup.formRows
    },
    paymentFormRows () {
      return this.configurationPayment.formRows
    },
    orderAssociations () {
      if (this.deliveryAssociations) {
        return this.deliveryAssociations.filter(association => association.type === 'relatedToOrder')
      }
      return null
    },
    itemsAndAssociationsLoaded () {
      return Array.isArray(this.items) && Array.isArray(this.orderAssociations)
    },
    associatedOrders () {
      const orders = []
      if (this.orderAssociations) {
        this.orderAssociations.map(association => {
          const residue = this.associatedOrderResidue(association)
          orders.push({
            'id': association.target.id,
            'name': this.$t('label.deliveryPaymentAssociatedOrder', { id: association.target.id, title: association.target.title, residue: residue.toFixed(2) }),
            'disabled': residue <= 0
          })
        })
      }
      return orders
    },
    predefinedPaymentTypeFormFields () {
      const columns = L.cloneDeep(this.configuration.predefinedPayment.dataTable.columns)

      this.predefinedPaymentsDTO.type = null
      if (this.items && this.items.length > 0) {
        columns.type.options = columns.type.options.filter(option => option.id !== 'deliveryGroupsPayments')
        columns.defaultValue = columns.type.options[0].id
        this.predefinedPaymentsDTO.type = columns.type.options[0].id
      }

      if (columns.order) {
        columns.order.options = this.associatedOrders
      }
      return columns
    },
    predefinedPaymentTypes () {
      return this.configuration.predefinedPayment.dataTable.predefinedPaymentTypes
    },
    predefinedTypeSelected () {
      return this.predefinedPaymentTypes.find(type => type.id === this.predefinedPaymentsDTO.type)
    },
    wpGroups () {
      const items = this.computedWpGroupItems.map(item => {
        return {
          computedSequence: item.computedSequence,
          name: item.name,
          endDate: item.endDate,
          startDate: item.startDate
        }
      })
      const wpGroups = L.uniqWith(items, (item1, item2) => {
        return item1.computedSequence === item2.computedSequence
      })
      return wpGroups
    },
    groupFormFields () {
      const columns = L.cloneDeep(this.configurationGroup.columns)
      if (this.wpGroups?.length > 0) {
        const wpGroups = this.wpGroups.map(wpGroup => {
          return {
            'id': wpGroup.name,
            'text': wpGroup.name
          }
        })
        columns.name.options = wpGroups
      }
      return columns
    },
    paymentFormFields () {
      const columns = L.cloneDeep(this.configurationPayment.columns)
      if (this.items && columns.deliveryGroup) {
        const deliveryGroups = this.items.map(item => {
          return {
            'name': item.deliveryGroupName,
            'id': item.deliveryGroupId,
            'trackerId': this.trackerId
          }
        })
        columns.deliveryGroup.options = deliveryGroups
      }
      if (columns.order) {
        columns.order.options = this.associatedOrders
      }
      return columns
    },
    groupApi () {
      return this.$axios.agilisUrlDeliveryGroup(this.applicationId, this.trackerTypeId, this.trackerId)
    },
    amountDue () {
      const amountAlreadyPaid = this.paymentGroupData?.table?.amount || 0
      const totalWp = this.computedWpGroupData?.table?.total || 0
      return L.round(totalWp - amountAlreadyPaid, 2)
    },
    canInsertPayment () {
      return this.amountDue > 0
    }
  },

  methods: {
    // override ineherited of add new methods
    ...mapActions({
      notifyError: 'notificationStore/notifyError',
      notifyWarning: 'notificationStore/notifyWarning',
      startLoader: 'loaderStore/startLoader',
      stopLoader: 'loaderStore/stopLoader'
    }),

    emptyGroupDTO () {
      return Configuration.datatable.getDTO(this.configurationGroup)
    },

    emptyPaymentDTO () {
      return Configuration.datatable.getDTO(this.configurationPayment)
    },

    associatedOrderPaymentIsValid (orderPaymentDetails) {
      const orderAssociation = this.orderAssociations.find(association => association.target.id === orderPaymentDetails.orderId)
      const residue = this.associatedOrderResidue(orderAssociation)
      return L.round(orderPaymentDetails.amount, 2) <= L.round(residue, 2)
    },

    associatedOrderResidue (orderAssociation) {
      const residueJsLogic = this.configurationOrderResidueJsonLogic
      const residueJsLogicParams = {
        tracker: orderAssociation.target,
        deliveryPayments: this.associatedOrderPayments.filter(payment => payment.orderId === orderAssociation.target.id)
      }
      return jsonLogic.apply(residueJsLogic, residueJsLogicParams)
    },

    async fetchData () {
      this.startLoader({ id: 'fetch-data' })
      const body = {}
      body.filterTerms = [
        {
          field: 'deliveryId',
          operator: 'EQUALS',
          value: this.trackerId
        }
      ]
      try {
        // Load group
        const url = this.$refs.dataTableWithGroups.searchApi
        const response = await this.$axios.post(url, body, this.$axios.agilisRequestConfig(url))
        this.items = response.data.items
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.stopLoader({ id: 'fetch-data' })
      }
    },

    checkMissingOrderItems () {
      if (this.items === null) return
      if (this.orderAssociations === null) return
      const paymentsWithOrder = this.items.filter(item => item.orderId)
      const orderIds = this.orderAssociations.map(association => association.target.id)
      const missingPaymentOrder = paymentsWithOrder.find(payment => !orderIds.includes(payment.orderId))
      if (missingPaymentOrder) {
        this.notifyWarning({ 'message': this.$t('sales.error.missingDeliveryOrderAssociation', { 'trackerId': missingPaymentOrder.orderId }) })
      }
    },

    async savePredefinedPayments () {
      this.startLoader({ id: 'save-payments' })
      const hasAssociatedOrder = !!this.predefinedPaymentsDTO?.order?.id
      let orderPaymentDetails = {
        orderId: this.predefinedPaymentsDTO?.order?.id,
        amount: this.amountDue
      }

      if (hasAssociatedOrder && !this.associatedOrderPaymentIsValid(orderPaymentDetails)) {
        this.stopLoader({ id: 'save-payments' })
        const error = { message: this.$t('message.deliveryPaymentOrderResidueWarning') }
        this.notifyWarning(error)
        return
      }

      const groupsToCreate = this.getPredefinedGroups()
      const createdGroups = await this.createPredefinedGroups(groupsToCreate)
      await this.createPredefinedPayments(createdGroups)

      this.fetchData()
      if (hasAssociatedOrder) this.fetchAssociatedOrderPayments()
      this.stopLoader({ id: 'save-payments' })
      this.closePredefinedDialog()
    },

    getPredefinedGroups () {
      let predefinedGroups = []
      let sequence = this.paymentGroupData?.table?.sequence || 0
      if (this.predefinedTypeSelected.id === 'deliveryGroupsPayments') {
        this.wpGroups
          .sort((a, b) => (a.computedSequence - b.computedSequence))
          .forEach(wpGroup => {
            let group = L.cloneDeep(this.predefinedTypeSelected.group)
            group.name = wpGroup.name
            group.wpGroup = wpGroup
            group.sequence = ++sequence
            group = this.addContext(group)
            predefinedGroups.push(group)
          })
      } else {
        let group = L.cloneDeep(this.predefinedTypeSelected.group)
        group.name = this.trackerDTO.title || this.$t(this.configurationTrackerType.name)
        group.sequence = ++sequence
        group = this.addContext(group)
        predefinedGroups.push(group)
      }
      return predefinedGroups
    },

    async createPredefinedGroups (groups) {
      const api = this.groupApi
      const url = `${api}`
      const responses = []
      for (let group of groups) {
        group = Configuration.datatable.mapItemDTOForSave(this.configurationGroup, group)
        await this.$axios.post(url, group, this.$axios.agilisRequestConfig(url))
          .then(response => {
            responses.push(response.data)
          })
          .catch(error => {
            this.notifyError(error)
          })
      }
      const createdGroups = responses.map(response => {
        const createdGroup = response
        const originGroup = groups.find(group => group.name === createdGroup.name)
        createdGroup.wpGroup = originGroup?.wpGroup
        return createdGroup
      })
      return createdGroups
    },

    async createPredefinedPayments (groups) {
      if (groups) {
        const createdPayments = []

        for (const group of groups) {
          const api = this.$axios.agilisUrlDeliveryGroupPayment(
            this.applicationId,
            this.trackerTypeId,
            this.trackerId,
            group.id
          )
          const url = `${api}`

          if (this.predefinedTypeSelected.id === 'deliveryGroupsPayments') {
            this.predefinedPaymentsDTO.count = 1
          }
          if (this.predefinedTypeSelected.id === 'multiplePayments') {
            this.predefinedPaymentsDTO.frequency = null
          }

          let paymentsCount = this.predefinedPaymentsDTO.count || 1
          let date = null
          for (var count = 1; count <= paymentsCount; count++) {
            let payment = {}
            payment.deliveryId = { id: this.trackerId }
            group.trackerId = this.trackerId
            payment.deliveryGroup = group
            payment.name = this.$t('sales.label.deliveryPredefinedPayment', { count: count })

            if (this.predefinedTypeSelected.id === 'deliveryGroupsPayments') {
              payment.dueDate = group.wpGroup.endDate || moment().startOf('day').format('YYYY-MM-DD')
              payment.amount = this.computedWpGroupData?.groups?.[group.wpGroup.computedSequence].total || 0
            } else {
              if (this.predefinedPaymentsDTO.frequency) {
                if (count === 1) {
                  date = this.predefinedPaymentsDTO.startDate
                } else {
                  date = moment(date).add(this.predefinedPaymentsDTO.frequency.months, 'month').format('YYYY-MM-DD')
                }
              } else {
                date = this.computedWpGroupData?.table?.endDate
              }

              let amount = this.amountDue

              if (this.predefinedPaymentsDTO.count > 1) {
                amount = amount / this.predefinedPaymentsDTO.count
              }

              payment.dueDate = date || moment().startOf('day').format('YYYY-MM-DD')
              payment.amount = amount
            }

            if (this.predefinedPaymentsDTO.order) {
              payment.order = this.predefinedPaymentsDTO.order
            }

            payment = this.addContext(payment)
            payment = Configuration.datatable.mapItemDTOForSave(this.configurationPayment, payment)

            await this.$axios.post(url, payment, this.$axios.agilisRequestConfig(url))
              .then(response => {
                createdPayments.push(response.data)
              })
              .catch(error => {
                this.notifyError(error)
              })
          }
        }
        return createdPayments
      }
    },

    closePredefinedDialog () {
      this.predefinedDialog = false
    },

    async saveGroup (_group) {
      this.startLoader({ id: 'save-group' })
      const api = this.groupApi

      setTimeout(async () => {
        let group = L.cloneDeep(_group) // work on a copy of the object to persist data in case of errors
        group = this.addContext(group)
        group = Configuration.datatable.mapItemDTOForSave(this.configurationGroup, group)

        this.startLoader({ id: 'save-group' })
        if (group.id) {
          const url = `${api}/${group.id}`
          try {
            await this.$axios.put(url, group, this.$axios.agilisRequestConfig(url))
            this.fetchData()
          } catch (error) {
            this.notifyError(error)
          }
          this.stopLoader({ id: 'save-group' })
          this.closeGroupDialog()
        } else {
          const url = `${api}`
          try {
            await this.$axios.post(url, group, this.$axios.agilisRequestConfig(url))
            this.fetchData()
          } catch (error) {
            this.notifyError(error)
          }
          this.stopLoader({ id: 'save-group' })
          this.closeGroupDialog()
        }
      }, 0)
    },

    groupLength (items) {
      const validItems = items.filter(item => item[this.itemsKey])
      return validItems.length
    },

    handleDeleteGroup (items, groupId) {
      if (this.groupLength(items) === 0) {
        this.deleteGroup(items, groupId)
        return
      }

      const userConfirmPromise = this.notifyUserConfirm({
        title: this.computedDeleteTitle,
        messages: [this.$t('sales.label.deliveryInvoiceGroupDeleteMessage')],
        confirm: this.deleteNo,
        cancel: this.deleteYes
      })

      userConfirmPromise.then((userResponse) => {
        if (!userResponse) {
          this.deleteGroup(items, groupId)
        }
      })
    },

    deleteGroup (items, groupId) {
      this.startLoader({ id: 'delete-group' })
      let item = items.find(item => item[this.$refs.dataTableWithGroups.groupingColumnId] === groupId)
      const api = this.groupApi
      const url = `${api}/${item.deliveryGroupId}`
      this.$axios.delete(
        url,
        {
          ...this.$axios.agilisRequestConfig(url),
          data: groupId
        })
        .then((response) => {
          this.fetchData()
          this.fetchAssociatedOrderPayments()
        })
        .catch((error) => {
          this.notifyError(error)
          this.stopLoader({ id: 'delete-group' })
        })
    },

    async fetchAssociatedOrderPayments () {
      this.startLoader({ id: 'fetch-data' })

      this.associatedOrderPayments = []
      this.orderAssociations.forEach((orderAssociation, key) => {
        const body = {}
        body.filterTerms = [
          {
            field: 'order',
            operator: 'EQUALS',
            value: orderAssociation.target.id
          }
        ]
        // Load group
        const url = this.$axios.agilisUrlQuery(this.applicationId, this.configuration.querySelectMode)
        this.$axios.post(url, body, this.$axios.agilisRequestConfig(url)).then((response) => {
          this.associatedOrderPayments.push(...response.data.items)
        }).catch((error) => {
          this.notifyError(error)
        })
      })
      this.stopLoader({ id: 'fetch-data' })
    },

    editGroup (items, groupId) {
      let item = items.find(item => item[this.$refs.dataTableWithGroups.groupingColumnId] === groupId)
      const url = `${this.groupApi}/${item.deliveryGroupId}`

      Vue.axios.get(url, Vue.axios.agilisRequestConfig(url)).then(response => {
        this.groupDTO = Object.assign(this.emptyGroupDTO(), response.data)
        this.groupDialog = true
      }).catch(error => {
        this.notifyError(error)
      })
    },

    closeGroupDialog () {
      this.groupDialog = false
      this.groupDTO = this.emptyGroupDTO()
    },

    async savePayment (_payment) {
      this.startLoader({ id: 'save-payment' })
      _payment.amount = L.round(_payment.amount, 2)
      let deliveryWpGroupTotal = L.round(this.computedWpGroupData?.table?.total, 2)
      let deliveryWpGroupCurrency = ''
      if (this.computedWpGroupItems.length > 0) {
        deliveryWpGroupCurrency = this.computedWpGroupItems[0].currency
      }
      const currency = deliveryWpGroupCurrency || 'EUR' // EUR is default if not set
      let oldPaymentGroupTotal = L.round(this.paymentGroupData?.table?.amount, 2)
      let newPaymentGroupTotal = 0
      let editedPaymentGroupTotal = 0
      let maxAmount = deliveryWpGroupTotal - oldPaymentGroupTotal
      let amountToCheckOnOrder = _payment.amount
      let hasAssociatedOrder = false
      if (_payment.order?.id) {
        const orderAssociation = this.orderAssociations.find(assocation => assocation.target.id === _payment.order.id)
        if (!orderAssociation) {
          _payment.order = null
        } else {
          hasAssociatedOrder = true
        }
      }

      if (_payment.id) {
        let originalPayment = null
        if (this.items && this.items.length > 0) {
          originalPayment = this.items.find(item => item.id === _payment.id)
        }
        if (originalPayment) {
          editedPaymentGroupTotal = oldPaymentGroupTotal - originalPayment.amount
          newPaymentGroupTotal = editedPaymentGroupTotal + _payment.amount
          maxAmount = deliveryWpGroupTotal - editedPaymentGroupTotal
          if (hasAssociatedOrder && _payment.order.id === originalPayment.order?.id) {
            amountToCheckOnOrder = _payment.amount - originalPayment.amount
          }
        }
      } else {
        newPaymentGroupTotal = oldPaymentGroupTotal + _payment.amount
      }

      let orderPaymentDetails = {
        orderId: _payment.order?.id,
        amount: amountToCheckOnOrder
      }
      if (hasAssociatedOrder && !this.associatedOrderPaymentIsValid(orderPaymentDetails)) {
        this.stopLoader({ id: 'save-payment' })
        const error = { message: this.$t('message.deliveryPaymentOrderResidueWarning') }
        this.notifyWarning(error)
        return
      }

      maxAmount = L.round(maxAmount, 2)
      newPaymentGroupTotal = L.round(newPaymentGroupTotal, 2)
      deliveryWpGroupTotal = L.round(deliveryWpGroupTotal, 2)
      if (newPaymentGroupTotal > deliveryWpGroupTotal) {
        let error = { message: this.$t('message.deliveryPaymentAmountWarning', { amount: maxAmount, currency: currency }) }
        this.stopLoader({ id: 'save-payment' })
        this.notifyWarning(error)
        return
      }
      setTimeout(async () => {
        let payment = L.cloneDeep(_payment) // work on a copy of the object to persist data in case of errors
        payment = this.addContext(payment)
        payment = Configuration.datatable.mapItemDTOForSave(this.configurationPayment, payment)

        const api = this.$axios.agilisUrlDeliveryGroupPayment(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId,
          payment.deliveryGroup.id)

        this.startLoader({ id: 'save-payment' })
        if (payment.id) {
          const url = `${api}/${payment.id}`
          try {
            await this.$axios.put(url, payment, this.$axios.agilisRequestConfig(url))
            this.fetchData()
            this.fetchAssociatedOrderPayments()
          } catch (error) {
            this.notifyError(error)
          }
          this.stopLoader({ id: 'save-payment' })
          this.closePaymentDialog()
        } else {
          const url = `${api}`
          try {
            await this.$axios.post(url, payment, this.$axios.agilisRequestConfig(url))
            this.fetchData()
            this.fetchAssociatedOrderPayments()
          } catch (error) {
            this.notifyError(error)
          }
          this.stopLoader({ id: 'save-payment' })
          this.closePaymentDialog()
        }
      }, 0)
    },

    handleDeletePayment (payment) {
      const userConfirmPromise = this.notifyUserConfirm({
        title: this.computedDeleteTitle,
        messages: [this.$t('sales.label.deliveryInvoiceDeleteMessage')],
        confirm: this.deleteNo,
        cancel: this.deleteYes
      })

      userConfirmPromise.then((userResponse) => {
        if (!userResponse) {
          this.deletePayment(payment)
        }
      })
    },

    deletePayment (payment) {
      this.startLoader({ id: 'delete-payment' })
      const api = this.$axios.agilisUrlDeliveryGroupPayment(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        payment.deliveryGroupId)
      const url = `${api}/${payment.id}`
      this.$axios.delete(
        url,
        {
          ...this.$axios.agilisRequestConfig(url),
          data: payment
        })
        .then((response) => {
          this.fetchData()
          this.fetchAssociatedOrderPayments()
        })
        .catch((error) => {
          this.notifyError(error)
          this.stopLoader({ id: 'delete-payment' })
        })
    },

    createPayment (group, items) {
      let item = items.find(item => item[this.$refs.dataTableWithGroups.groupingColumnId] === group)
      this.paymentDTO = this.emptyPaymentDTO()
      this.paymentDTO.deliveryGroup = item.deliveryGroup
      this.paymentDialog = true
    },

    editPayment (payment) {
      this.paymentDTO = Object.assign(this.emptyPaymentDTO(), L.cloneDeep(payment))
      this.paymentDialog = true
    },

    copyPayment (payment) {
      this.paymentDTO = L.cloneDeep(payment)
      this.paymentDTO.id = null
      this.paymentDialog = true
    },

    closePaymentDialog () {
      this.paymentDialog = false
      this.paymentDTO = this.emptyPaymentDTO()
    },

    addContext (item) {
      return Configuration.addContext(this.selectedContext, item)
    },

    userCanDoAndAccessFeature (feature, operation) {
      return Configuration.auth.userCanDoAndAccessFeature(this.selectedContext, feature, operation, this.trackerDTO, this.userId)
    },

    requestGroupDataReload (paymentGroupData) {
      this.paymentGroupData = paymentGroupData
    }
  }
}
